import type { ICajaResume } from '@/interfaces/ICajaResume';
import type { ICajaMovement } from '@/interfaces/ICajaMovement';
import type { IClubActivityItem } from '@/interfaces/IClubActivity';
import type { IClubMember } from '@/interfaces/IClubMember';
import type { IClubProgram } from '@/interfaces/IClubProgram';
import type { ICustomer } from '@/interfaces/ICustomer';
import type { IOrder } from '@/interfaces/IOrder';
import type { IOrderField } from '@/interfaces/IOrderField';
import type { IOrderItem } from '@/interfaces/IOrderItem';
import type { ITable } from '@/interfaces/ITable';
import type { IReservation } from '@/interfaces/IReservation';
import type { IReservationGuest } from '@/interfaces/IReservationGuest';
import type { IReservationWaitlist } from '@/interfaces/IReservationWaitlist';
import type { IGhUntrustedValue } from '../gpthelp-host-contract';
import { clampText } from '../cards/card-text';

/** Longest customer-written text a tool result carries (host contract §3.2). */
export const UNTRUSTED_MAX_LENGTH = 280;

/**
 * Marks text written by customers (names, notes, addresses) so GPTHelp treats
 * it as data, never as instructions, and taints the conversation. Trimmed and
 * capped at UNTRUSTED_MAX_LENGTH characters; null when there is no text.
 */
export function untrusted(
  value: string | null | undefined
): IGhUntrustedValue | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  return {
    untrusted: Array.from(text).slice(0, UNTRUSTED_MAX_LENGTH).join(''),
  };
}

/** A phone number as people type it: digits, «+», spaces, dots, dashes, brackets. */
const PHONE_SHAPE = /^[+\d\s().-]{3,32}$/;
/** A plain email address. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function contactText(
  value: string | null | undefined,
  shape: RegExp
): string | IGhUntrustedValue | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  return shape.test(text) ? text : untrusted(text);
}

/**
 * A phone customers type themselves: plain when it looks like a phone,
 * wrapped as untrusted otherwise (a «phone» holding instructions).
 */
export function phoneText(
  value: string | null | undefined
): string | IGhUntrustedValue | null {
  return contactText(value, PHONE_SHAPE);
}

/** An email customers type themselves: plain when it looks like an email, wrapped otherwise. */
export function emailText(
  value: string | null | undefined
): string | IGhUntrustedValue | null {
  return contactText(value, EMAIL_SHAPE);
}

/** A value made by `untrusted()`: a plain object whose only key is the text. */
export function isUntrustedValue(value: unknown): value is IGhUntrustedValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 1 &&
    'untrusted' in value &&
    typeof value.untrusted === 'string'
  );
}

/**
 * Shallow-removes null/undefined values so tool outputs stay token-lean.
 * Absent fields mean null/none by convention (documented in _semantics).
 */
export function omitNullish<T extends Record<string, unknown>>(
  obj: T
): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    const value = obj[key];
    if (value === null || typeof value === 'undefined') continue;
    out[key] = value;
  }
  return out;
}

/** Caps a list at `limit` rows, flagging that more rows exist. */
export function capList<T>(
  rows: T[],
  limit: number
): { results: T[]; truncated?: true } {
  if (rows.length <= limit) return { results: rows };
  return { results: rows.slice(0, limit), truncated: true };
}

type ICajaResumeCanceledOrder = ICajaResume['canceledOrdersDetails'][number];

/**
 * Omit-based: every future ICajaResume field flows through automatically;
 * only the PII redaction (canceled orders carry customer name/address) stays
 * explicit.
 */
export function sanitizeCajaResume(resume: ICajaResume) {
  const { canceledOrdersDetails, ...safe } = resume;
  return {
    ...safe,
    canceledOrders: canceledOrdersDetails?.map(
      (row: ICajaResumeCanceledOrder) => ({
        id: row.id,
        createdAt: row.createdAt,
        cancelledAt: row.cancelledAt,
        total: row.total,
        items: row.items,
        paymentMethod: row.paymentMethod,
        type: row.type,
        tableName: row.tableName,
        // customerName / customerAddress intentionally dropped (PII)
      })
    ),
  };
}

/**
 * POSITIONAL bucket order copied verbatim from the API's
 * parseOrderPaymentsByType (src/utilities/orders.utils.ts). Stored order rows
 * depend on these indexes — append-only, NEVER reorder.
 */
const PAYMENTS_BY_TYPE_ORDER = [
  'cash',
  'card',
  'online',
  'qr',
  'invitation',
  'employee',
  'customer',
  'banktransfer',
  'club',
] as const;

/** Decodes the positional paymentsByType array into a named non-zero map. */
export function decodePaymentsByType(
  values: number[] | null | undefined
): Record<string, number> | null {
  if (!values || !values.length) return null;
  const out: Record<string, number> = {};
  values.forEach((amount, index) => {
    const bucket = PAYMENTS_BY_TYPE_ORDER[index];
    if (!bucket || !amount) return;
    out[bucket] = amount;
  });
  return Object.keys(out).length ? out : null;
}

/** Compact order-list row (orders_find / tables_getSessionOrders). */
export function sanitizeOrderRow(order: IOrder) {
  return omitNullish({
    id: order.id,
    humanId: order.humanId ?? null,
    createdAt: order.createdAt ?? null,
    type: order.type,
    status: order.status,
    paymentStatus: order.paymentStatus,
    paymentMethod: order.paymentMethod ?? null,
    total: order.total,
    totalPaid: order.totalPaid,
    tipTotal: order.tipTotal ?? null,
    discountedAmount: order.discountedAmount ?? null,
    table: order.table ? { id: order.table.id, name: order.table.name } : null,
    covers: order.tableSession?.covers ?? order.covers ?? null,
    customerName: untrusted(order.userName),
    customerPhone: phoneText(order.userWhatsapp ?? order.userTel),
    cajaId: order.cajaId ?? null,
  });
}

export function sanitizeOrderItem(item: IOrderItem) {
  return omitNullish({
    index: item.index ?? null,
    itemId: item.itemId,
    name: item.item?.name ?? null,
    price: item.price,
    amount: item.amount,
    total: item.total,
    comment: untrusted(item.comment),
    canceledAt: item.canceledAt ?? null,
    options: item.options?.length
      ? item.options.map((opt) =>
          omitNullish({
            optionItemId: opt.optionItemId,
            title: opt.optionItem?.title ?? null,
            val: opt.val,
            price: opt.price,
            total: opt.total,
          })
        )
      : null,
  });
}

/**
 * orders_find row with its item lines (includeItems). Same `orderItems` key
 * and line shape as sanitizeOrderDetail, so the model sees ONE shape for
 * order lines regardless of the tool that produced them.
 */
export function sanitizeOrderRowWithItems(order: IOrder) {
  return {
    ...sanitizeOrderRow(order),
    orderItems: order.orderItems?.map(sanitizeOrderItem) ?? [],
  };
}

/** Order custom-field answer: the value is typed by the customer. */
function sanitizeOrderField(field: IOrderField) {
  return omitNullish({ ...field, value: untrusted(field.value) });
}

/**
 * Full order detail (orders_get) — omit-based so future fields flow; the
 * explicit destructure drops relations, device/print internals and geo-PII,
 * and re-adds the customer-written texts wrapped as untrusted.
 */
export function sanitizeOrderDetail(order: IOrder) {
  const {
    user,
    restaurant,
    branch,
    userId,
    tableId,
    customerId,
    clubMemberId,
    deviceId,
    posDeviceId,
    localId,
    deliveryLat,
    deliveryLng,
    printedPaidAt,
    localUpdating,
    groupPayment,
    sessionId,
    cajaStatusId,
    updatedAt,
    customer,
    table,
    clubMember,
    tableSession,
    orderItems,
    orderFields,
    paymentsByType,
    userName,
    userTel,
    userWhatsapp,
    userAddress,
    userAddress2,
    userAddressComments,
    comment,
    ...safe
  } = order;

  return omitNullish({
    ...safe,
    userName: untrusted(userName),
    userTel: phoneText(userTel),
    userWhatsapp: phoneText(userWhatsapp),
    userAddress: untrusted(userAddress),
    userAddress2: untrusted(userAddress2),
    userAddressComments: untrusted(userAddressComments),
    comment: untrusted(comment),
    covers: tableSession?.covers ?? order.covers ?? null,
    table: table ? { id: table.id, name: table.name } : null,
    tableSession: tableSession
      ? omitNullish({ id: tableSession.id, covers: tableSession.covers })
      : null,
    customer: customer
      ? omitNullish({
          id: customer.id,
          name: untrusted(customer.name),
          phone: phoneText(customer.tel),
        })
      : null,
    clubMember: clubMember
      ? {
          id: clubMember.id,
          memberNumber: clubMember.memberNumber,
          name: untrusted(clubMember.name),
        }
      : null,
    orderItems: orderItems?.map(sanitizeOrderItem) ?? null,
    orderFields: orderFields?.length
      ? orderFields.map(sanitizeOrderField)
      : null,
    payments: decodePaymentsByType(paymentsByType),
  });
}

/** Caja movement row attached to an order (orders_get includeMovements). */
export function sanitizeCajaMovement(movement: ICajaMovement) {
  return omitNullish({
    type: movement.type,
    amount: movement.amount,
    paymentMethod: movement.paymentMethod ?? null,
    cajaId: movement.cajaId ?? null,
    reimbursed: movement.reimbursed || null,
    description: movement.description ?? null,
    createdAt: movement.createdAt ?? null,
  });
}

interface ICouponUseRow {
  orderId: number;
  discountAmount: number;
  usedAt: Date | string;
  customer?: { id?: number; name?: string } | null;
}

/** Coupon usage row (coupons_getUses) — nested order/user embeds dropped. */
export function sanitizeCouponUse(use: ICouponUseRow) {
  return omitNullish({
    orderId: use.orderId,
    discountAmount: use.discountAmount,
    usedAt: use.usedAt,
    customer: use.customer
      ? omitNullish({
          id: use.customer.id ?? null,
          name: untrusted(use.customer.name),
        })
      : null,
  });
}

/**
 * Program-feed / member-activity ledger row. Drops only `id` (per-ledger row
 * id, ambiguous across the points/cash ledgers — pagination rides the cursor).
 */
export function sanitizeClubActivityRow(row: IClubActivityItem) {
  return omitNullish({
    source: row.source,
    type: row.type,
    points: row.points,
    amount: row.amount,
    bucket: row.bucket,
    balanceAfter: row.balanceAfter,
    orderId: row.orderId,
    note: row.note,
    actorType: row.actorType,
    actorName: row.actorName,
    createdAt: row.createdAt,
    memberId: row.memberId ?? null,
    memberName: untrusted(row.memberName),
    memberNumber: row.memberNumber ?? null,
  });
}

/** Club member record (club_findMembers / club_getMember): name and contact are customer-written. */
export function sanitizeClubMember(member: IClubMember) {
  return omitNullish({
    ...member,
    name: untrusted(member.name),
    email: emailText(member.email),
    phone: phoneText(member.phone),
  });
}

/** Characters of the club terms a result keeps (the owner's own text). */
export const CLUB_TERMS_MAX = 600;

/** A decimal column may arrive as a string from the driver. */
export function decimalOf(
  value: number | string | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

/**
 * Club program rules (club_summary, plan §8 R3) — KEEP-LIST: ids, timestamps
 * and any future column stay out. `branches` names the branches the program
 * runs in (every branch of the store when it is not limited), as the club
 * page lists them; null when the store never activated a program.
 */
export function sanitizeClubProgram(
  program: IClubProgram | null,
  branchNames: ReadonlyMap<number, string>
) {
  if (!program) return null;
  const branchIds = program.branchIds?.length
    ? program.branchIds
    : [...branchNames.keys()];
  const terms = program.termsText
    ? clampText(program.termsText, CLUB_TERMS_MAX)
    : '';
  return omitNullish({
    status: program.status,
    name: program.name,
    pointsEnabled: program.pointsEnabled,
    earnUnitAmount: decimalOf(program.earnUnitAmount),
    earnPoints: program.earnPoints,
    redeemPointsCost: program.redeemPointsCost,
    redeemValue: decimalOf(program.redeemValue),
    minRedeemPoints: program.minRedeemPoints,
    maxRedeemPercent: program.maxRedeemPercent,
    pointsExpiryMonths: program.pointsExpiryMonths,
    welcomePoints: program.welcomePoints,
    balanceEnabled: program.balanceEnabled,
    topUpPlans:
      program.topUpPlans?.map((plan) =>
        omitNullish({
          amount: decimalOf(plan.amount),
          bonus: decimalOf(plan.bonus),
        })
      ) ?? null,
    minTopUp: decimalOf(program.minTopUp),
    maxMemberBalance: decimalOf(program.maxMemberBalance),
    branches: branchIds.flatMap((id) => {
      const name = branchNames.get(id);
      return name === undefined ? [] : [name];
    }),
    termsText: terms || null,
  });
}

/**
 * Customer row — deliberate KEEP-LIST exception to the omit convention: a
 * future server field on Customer is more likely PII (geo, linked accounts)
 * than a harmless metric. lat/lng/user/userId stay dropped forever.
 */
export function sanitizeCustomerRow(customer: ICustomer) {
  return omitNullish({
    id: customer.id,
    nativeId: customer.nativeId,
    customId: customer.customId,
    name: untrusted(customer.name),
    taxIdType: customer.taxIdType,
    taxId: customer.taxId,
    /** decimal(16,2) may arrive as a string from the driver. */
    balance: Number(customer.balance ?? 0),
    tel: phoneText(customer.tel),
    email: emailText(customer.email),
    address: untrusted(customer.address),
    address2: untrusted(customer.address2),
    addressComment: untrusted(customer.addressComment),
    createdAt: customer.createdAt,
  });
}

/**
 * Branch table config row (tables_list). MUST drop `token` (live QR ordering
 * credential) and the floor-plan geometry (shape/posX/posY/w/h — no support
 * value).
 */
export function sanitizeTableRow(
  table: ITable,
  sectorsById: Map<number, { id: number; name: string }>
) {
  const sector =
    table.sectorId !== null && table.sectorId !== undefined
      ? sectorsById.get(table.sectorId) ?? null
      : null;
  return omitNullish({
    id: table.id,
    name: table.name,
    description: table.description || null,
    capacity: table.capacity,
    enabled: table.enabled,
    sector: sector ? { id: sector.id, name: sector.name } : null,
  });
}

/** Booking KEEP-LIST: tokens, linked sessions/accounts, allergy data and future fields never flow through. */
export function sanitizeReservationRow(row: IReservation) {
  return omitNullish({
    id: row.id,
    code: row.code,
    guestName: untrusted(row.guestName),
    guestPhone: phoneText(row.guestPhone),
    guestEmail: emailText(row.guestEmail),
    partySize: row.partySize,
    startsAt: row.startsAt,
    endsAt: row.endsAt,
    serviceDate: row.serviceDate,
    durationMin: row.durationMin,
    status: row.status,
    source: row.source,
    sectorId: row.sectorId,
    notesGuest: untrusted(row.notesGuest),
    notesInternal: row.notesInternal,
    occasion: untrusted(row.occasion),
    tags: row.tags,
    depositRequired: row.depositRequired,
    depositAmount: row.depositAmount,
    depositStatus: row.depositStatus,
    depositAppliedAmount: row.depositAppliedAmount,
    depositAppliedAt: row.depositAppliedAt,
    depositUnappliedAmount: row.depositUnappliedAmount,
    tablesAutoAssigned: row.tablesAutoAssigned,
    guestConfirmedAt: row.guestConfirmedAt,
    guestLateMinutes: row.guestLateMinutes,
    arrivedAt: row.arrivedAt,
    seatedAt: row.seatedAt,
    finishedAt: row.finishedAt,
    cancelledAt: row.cancelledAt,
    cancelledBy: row.cancelledBy,
    cancelReason: untrusted(row.cancelReason),
    autoClosed: row.autoClosed,
    createdByName: row.createdByName,
    createdAt: row.createdAt,
    tables: row.tables.map((table) =>
      omitNullish({
        id: table.id,
        name: table.name,
        capacity: table.capacity,
        sectorId: table.sectorId,
      })
    ),
    customer: row.customer
      ? omitNullish({
          id: row.customer.id,
          isClubMember: row.customer.isClubMember,
        })
      : null,
  });
}

/** Reservation guest KEEP-LIST: email, notes, account links and future fields stay private. */
export function sanitizeReservationGuestRow(row: IReservationGuest) {
  return omitNullish({
    id: row.customerId,
    name: untrusted(row.name),
    phone: phoneText(row.phoneE164),
    reservationsCount: row.reservationsCount,
    visitsCount: row.visitsCount,
    noShowsCount: row.noShowsCount,
    cancellationsCount: row.cancellationsCount,
    upcomingCount: row.upcomingCount,
    lastVisitAt: row.lastVisitAt,
    tags: row.tags,
  });
}

/** Waitlist KEEP-LIST, including only the identity/contact and operational data shown to staff. */
export function sanitizeWaitlistRow(row: IReservationWaitlist) {
  return omitNullish({
    id: row.id,
    kind: row.kind,
    guestName: untrusted(row.guestName),
    guestPhone: phoneText(row.guestPhone),
    guestEmail: emailText(row.guestEmail),
    partySize: row.partySize,
    sectorId: row.sectorId,
    quotedWaitMin: row.quotedWaitMin,
    desiredStartsAt: row.desiredStartsAt,
    desiredDate: row.desiredDate,
    status: row.status,
    notifiedAt: row.notifiedAt,
    seatedAt: row.seatedAt,
    reservationId: row.reservationId,
    notes: untrusted(row.notes),
    createdAt: row.createdAt,
  });
}
